import React, { forwardRef, useRef, useCallback, useEffect, useState } from "react";

export interface SliderProps {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  showValue?: boolean;
  label?: string;
  className?: string;
}

/**
 * Arc-style Slider component
 * 
 * Features:
 * - Smooth dragging
 * - Multiple sizes: sm, md, lg
 * - Optional value display
 * - Optional label
 * - Keyboard support
 */
const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      value,
      defaultValue = 0,
      min = 0,
      max = 100,
      step = 1,
      onChange,
      onChangeEnd,
      disabled = false,
      size = "md",
      showValue = false,
      label,
      className = "",
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);

    // Sync with controlled value
    useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value);
      }
    }, [value]);

    const currentValue = value ?? internalValue;
    const percentage = ((currentValue - min) / (max - min)) * 100;

    const updateValue = useCallback(
      (clientX: number) => {
        if (!trackRef.current || disabled) return;

        const rect = trackRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const rawValue = min + percent * (max - min);
        const steppedValue = Math.round(rawValue / step) * step;
        const clampedValue = Math.max(min, Math.min(max, steppedValue));

        if (value === undefined) {
          setInternalValue(clampedValue);
        }
        onChange?.(clampedValue);
      },
      [disabled, min, max, step, value, onChange]
    );

    const handleMouseDown = (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(true);
      updateValue(e.clientX);
    };

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (isDragging) {
          updateValue(e.clientX);
        }
      },
      [isDragging, updateValue]
    );

    const handleMouseUp = useCallback(() => {
      if (isDragging) {
        setIsDragging(false);
        onChangeEnd?.(currentValue);
      }
    }, [isDragging, currentValue, onChangeEnd]);

    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Keyboard support
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;

      let newValue = currentValue;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowUp":
          e.preventDefault();
          newValue = Math.min(max, currentValue + step);
          break;
        case "ArrowLeft":
        case "ArrowDown":
          e.preventDefault();
          newValue = Math.max(min, currentValue - step);
          break;
        case "Home":
          e.preventDefault();
          newValue = min;
          break;
        case "End":
          e.preventDefault();
          newValue = max;
          break;
        default:
          return;
      }

      if (value === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
      onChangeEnd?.(newValue);
    };

    // Size configurations
    const sizeConfig = {
      sm: {
        track: "h-1",
        thumb: "w-3.5 h-3.5 -mt-[5px]",
        label: "text-xs",
      },
      md: {
        track: "h-1.5",
        thumb: "w-5 h-5 -mt-[7px]",
        label: "text-sm",
      },
      lg: {
        track: "h-2",
        thumb: "w-6 h-6 -mt-[8px]",
        label: "text-sm",
      },
    };

    const config = sizeConfig[size];

    return (
      <div ref={ref} className={className}>
        {(label || showValue) && (
          <div className="flex items-center justify-between mb-2">
            {label && (
              <span className={`${config.label} font-medium text-content-primary`}>
                {label}
              </span>
            )}
            {showValue && (
              <span className={`${config.label} font-medium text-content-secondary`}>
                {currentValue}
              </span>
            )}
          </div>
        )}

        <div
          ref={trackRef}
          className={`
            relative w-full
            ${config.track}
            bg-surface-tertiary
            rounded-arc-full
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          `}
          onMouseDown={handleMouseDown}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={currentValue}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
        >
          {/* Filled track */}
          <div
            className={`
              absolute left-0 top-0 h-full
              bg-accent
              rounded-arc-full
              transition-[width] duration-75
            `}
            style={{ width: `${percentage}%` }}
          />

          {/* Thumb */}
          <div
            className={`
              absolute top-1/2
              ${config.thumb}
              bg-white
              border-2 border-accent
              rounded-full
              shadow-arc-md
              transition-transform duration-75
              ${isDragging ? "scale-110" : ""}
              ${disabled ? "" : "hover:scale-110"}
            `}
            style={{ left: `calc(${percentage}% - ${size === "sm" ? 7 : size === "md" ? 10 : 12}px)` }}
          />
        </div>
      </div>
    );
  }
);

Slider.displayName = "Slider";

export default Slider;









